import {Button} from "@zendeskgarden/react-buttons";
import {Field, Hint, Label, Radio} from "@zendeskgarden/react-forms";
import {Inline} from "@zendeskgarden/react-loaders";
import {Body, Close, Footer, FooterItem, Header, Modal} from "@zendeskgarden/react-modals";
import {Alert} from "@zendeskgarden/react-notifications";
import {ReactElement, useEffect, useState} from "react";
import {toast} from "react-toastify";
import {ThemeProvider} from "styled-components";
import {COLORS} from "../../../constants/Colors";
import {ViziblyTheme} from "../../../styles/zendesk-garden/ViziblyZDGTheme";

export type SetStatusPermissionLevel = "editOnly" | "readOnly" | "configureDriver" | null;

export interface SetStatusModalProps {
    visible: boolean;
    budgetYear: number;
    itemName: string[];
    itemIds: string[];
    itemLabel: string;
    itemLabelPlural: string;
    initialLocked: boolean;
    initialValuesLocked: boolean;
    submitting?: boolean;
    onSubmit: (permissionLevel: SetStatusPermissionLevel) => Promise<boolean>;
    onClose: () => void;
    onStatusSet: () => void;
}

export function SetStatusModal(props: SetStatusModalProps): ReactElement {
    const {
        visible,
        itemName,
        itemIds,
        itemLabel,
        itemLabelPlural,
        initialLocked,
        initialValuesLocked,
        submitting = false,
        onSubmit,
        onClose,
        onStatusSet
    } = props;

    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [permissionLevel, setPermissionLevel] = useState<SetStatusPermissionLevel>(null);

    useEffect(() => {
        if (visible) {
            let initialPermissionState: SetStatusPermissionLevel = null;

            if (initialValuesLocked) {
                initialPermissionState = "readOnly";
            } else if (initialLocked) {
                initialPermissionState = "editOnly";
            }

            setPermissionLevel(initialPermissionState);
        }
    }, [visible, initialLocked, initialValuesLocked]);

    const reset = () => {
        setPermissionLevel(null);
        onClose();
    };

    const handleSubmit = async () => {
        setIsSaving(true);

        try {
            const success = await onSubmit(permissionLevel);

            if (success) {
                toast.success(`${itemLabel} access updated successfully`);
                onStatusSet();
                reset();
            } else {
                toast.error(`Failed to update ${itemLabel.toLowerCase()} access`);
            }
        } catch {
            toast.error(`Error updating ${itemLabel.toLowerCase()} access`);
        } finally {
            setIsSaving(false);
        }
    };

    if (!visible) {
        return <></>;
    }

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <Modal onClose={reset}>
                {itemIds.length === 1 ?
                    <Header>
                        Set Access for "{itemName}" {itemLabel}
                    </Header>
                    :
                    <Header>
                        Set access for {itemIds.length} "{itemName.limit(3).join(", ")}{itemName.length > 3 && "..."}" {itemLabelPlural}
                    </Header>
                }
                <Body>
                    <h5>Set which actions can be performed by non-Admins on {itemIds.length === 1 ? "this" : "these"} {itemIds.length === 1 ? itemLabel.toLowerCase() : itemLabelPlural.toLowerCase()}.</h5>
                    <Field style={{marginTop: "1rem"}}>
                        <Radio
                            checked={permissionLevel === null}
                            onChange={() => setPermissionLevel(null)}
                        >
                            <Label>Full Access</Label>
                            <Hint>Non-Admins have full control over this {itemLabel.toLowerCase()}.</Hint>
                        </Radio>
                    </Field>
                    {itemLabel === "Operational Driver" &&
                        // dirty, but for now it's the only driver type that supports configLocked
                        <Field style={{marginTop: "0.75rem"}}>
                            <Radio
                                checked={permissionLevel === "configureDriver"}
                                onChange={() => setPermissionLevel("configureDriver")}
                            >
                                <Label>Configure Driver</Label>
                                <Hint>Non-Admins can change the name, ramp-up, and min/max values for this driver, as well as update assumption values, but cannot remove it.</Hint>
                            </Radio>
                        </Field>
                    }
                    <Field style={{marginTop: "0.75rem"}}>
                        <Radio
                            checked={permissionLevel === "editOnly"}
                            onChange={() => setPermissionLevel("editOnly")}
                        >
                            <Label>Edit-Only</Label>
                            <Hint>Non-Admins can edit values, but not remove this {itemLabel.toLowerCase()}.</Hint>
                        </Radio>
                    </Field>
                    <Field style={{marginTop: "0.75rem"}}>
                        <Radio
                            checked={permissionLevel === "readOnly"}
                            onChange={() => setPermissionLevel("readOnly")}
                        >
                            <Label>Read-Only</Label>
                            <Hint>Non-Admins cannot remove this {itemLabel.toLowerCase()} or edit its values.</Hint>
                        </Radio>
                    </Field>

                    <Alert type="info" style={{marginTop: "1rem", paddingTop: ".5rem", paddingBottom: ".5rem"}}>
                        These settings <b>will not</b> apply to users with the <b>"Modeling Methods Admin"</b> authorization.
                    </Alert>
                </Body>
                <Footer>
                    <FooterItem>
                        <Button onClick={reset} isBasic disabled={isSaving || submitting}>
                            Cancel
                        </Button>
                    </FooterItem>
                    <FooterItem>
                        <Button isPrimary onClick={handleSubmit} disabled={isSaving || submitting}>
                            {isSaving
                                ? <Inline size={24} color={COLORS.POSITIVE_COLOR} aria-label="loading"/>
                                : <span>Set Access</span>
                            }
                        </Button>
                    </FooterItem>
                </Footer>
                <Close aria-label="Close modal"/>
            </Modal>
        </ThemeProvider>
    );
}
